import { useState, useRef, useCallback, useEffect } from "react";

interface SwipeGestureConfig {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  threshold?: number;
}

export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  threshold = 120,
}: SwipeGestureConfig) {
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastMoveTimeRef = useRef(0);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    setIsDragging(true);
    startPosRef.current = { x: clientX, y: clientY };
    lastPosRef.current = { x: clientX, y: clientY };
    lastMoveTimeRef.current = Date.now();
    velocityRef.current = { x: 0, y: 0 };
  }, []);

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging) return;

      const now = Date.now();
      const timeDelta = now - lastMoveTimeRef.current;

      if (timeDelta > 0) {
        velocityRef.current = {
          x: (clientX - lastPosRef.current.x) / timeDelta,
          y: (clientY - lastPosRef.current.y) / timeDelta,
        };
      }

      lastPosRef.current = { x: clientX, y: clientY };
      lastMoveTimeRef.current = now;

      const deltaX = clientX - startPosRef.current.x;
      const deltaY = clientY - startPosRef.current.y;

      setOffset({ x: deltaX, y: deltaY });
      // Smoother rotation with less extreme values
      setRotation(deltaX * 0.08);
    },
    [isDragging]
  );

  const handleEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    // Calculate if swipe based on distance OR velocity
    const velocity = Math.abs(velocityRef.current.x);
    const distance = Math.abs(offset.x);

    // Only swipe if there was actual movement (not just a click)
    // Require minimum distance of 30px to distinguish from clicks
    const hadMovement = distance > 30;
    const shouldSwipe = hadMovement && (distance > threshold || velocity > 0.5);

    if (shouldSwipe) {
      setIsAnimatingOut(true);

      // Animate card off screen - continue in the direction it was swiped
      const direction = offset.x > 0 ? 1 : -1;
      // Use larger distance and keep the Y movement
      setOffset({ x: direction * (window.innerWidth * 1.5), y: offset.y * 2 });
      setRotation(direction * 30);

      // Call the swipe callback immediately
      const swipeCallback = offset.x > 0 ? onSwipeRight : onSwipeLeft;

      setTimeout(() => {
        // Call the swipe callback which will update currentIndex
        // This will unmount this component (due to key prop) and mount a fresh one
        swipeCallback();
        // If component stays mounted (no remount), reset local state after animation
        setTimeout(() => {
          if (!isMountedRef.current) return;
          setOffset({ x: 0, y: 0 });
          setRotation(0);
          setIsAnimatingOut(false);
        }, 50);
      }, 400);
    } else {
      // Snap back to center
      setOffset({ x: 0, y: 0 });
      setRotation(0);
    }
  }, [isDragging, offset, threshold, onSwipeLeft, onSwipeRight]);

  // Global event listeners for better drag handling
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };

    const handleGlobalMouseUp = () => {
      handleEnd();
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      // Prevent default to stop browser from scrolling or doing other gestures
      e.preventDefault();
      const touch = e.touches[0];
      if (touch) {
        handleMove(touch.clientX, touch.clientY);
      }
    };

    const handleGlobalTouchEnd = () => {
      handleEnd();
    };

    // Also handle touchcancel as end
    const handleGlobalTouchCancel = () => {
      handleEnd();
    };

    window.addEventListener("mousemove", handleGlobalMouseMove, { passive: false });
    window.addEventListener("mouseup", handleGlobalMouseUp);
    window.addEventListener("touchmove", handleGlobalTouchMove, { passive: false });
    window.addEventListener("touchend", handleGlobalTouchEnd);
    window.addEventListener("touchcancel", handleGlobalTouchCancel);

    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("touchmove", handleGlobalTouchMove);
      window.removeEventListener("touchend", handleGlobalTouchEnd);
      window.removeEventListener("touchcancel", handleGlobalTouchCancel);
    };
  }, [isDragging, handleMove, handleEnd]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Don't prevent default on buttons or interactive elements
      const target = e.target as HTMLElement;
      if (target.tagName === "BUTTON" || target.closest("button")) {
        return;
      }
      e.preventDefault();
      handleStart(e.clientX, e.clientY);
    },
    [handleStart]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    },
    [handleStart]
  );

  const getCardStyle = useCallback((): React.CSSProperties => {
    // Only fade out during animation out, not during dragging
    const dragOpacity = isAnimatingOut ? 1 - Math.abs(offset.x) / 400 : 1;

    // Only apply transition during animating out
    // No transition when idle or dragging
    let transition = "none";
    if (isAnimatingOut) {
      transition = "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
    }

    return {
      transform: `translate(${offset.x}px, ${offset.y * 0.5}px) rotate(${rotation}deg)`,
      opacity: isAnimatingOut ? Math.max(0, dragOpacity) : 1,
      transition,
      cursor: isDragging ? "grabbing" : "grab",
      userSelect: "none",
      touchAction: "none",
      // Stop CSS animation when dragging to prevent interference
      animation: isDragging ? "none" : undefined,
    };
  }, [isDragging, isAnimatingOut, offset, rotation]);

  const getOverlayOpacity = useCallback(
    (direction: "left" | "right") => {
      if (!isDragging && !isAnimatingOut) return 0;

      // Calculate base opacity based on swipe distance
      let baseOpacity = 0;
      if (direction === "left" && offset.x < 0) {
        baseOpacity = Math.min(Math.abs(offset.x) / 120, 1);
      } else if (direction === "right" && offset.x > 0) {
        baseOpacity = Math.min(offset.x / 120, 1);
      }

      // During animation out, fade out the overlay along with the card
      if (isAnimatingOut) {
        const fadeOutOpacity = 1 - Math.abs(offset.x) / 400;
        return Math.max(0, baseOpacity * fadeOutOpacity);
      }

      return baseOpacity;
    },
    [isDragging, isAnimatingOut, offset.x]
  );

  // Programmatically trigger swipe animation (for button clicks)
  const triggerSwipeAnimation = useCallback(
    (direction: "left" | "right") => {
      setIsAnimatingOut(true);

      // Animate card off screen in the specified direction
      const directionMultiplier = direction === "right" ? 1 : -1;
      setOffset({ x: directionMultiplier * (window.innerWidth * 1.5), y: 0 });
      setRotation(directionMultiplier * 30);

      // Call the appropriate callback
      const swipeCallback = direction === "right" ? onSwipeRight : onSwipeLeft;

      setTimeout(() => {
        swipeCallback();
        setTimeout(() => {
          if (!isMountedRef.current) return;
          setOffset({ x: 0, y: 0 });
          setRotation(0);
          setIsAnimatingOut(false);
        }, 50);
      }, 400);
    },
    [onSwipeLeft, onSwipeRight]
  );

  return {
    isDragging,
    isAnimatingOut,
    handlers: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart,
    },
    getCardStyle,
    getOverlayOpacity,
    triggerSwipeAnimation,
  };
}
